/**
 * Report translations: the network's own wording for the labels a report
 * prints — column headings, totals, dates. A translation starts from one of
 * the API's built-in languages and overrides only the labels it needs to; a
 * report template then picks the translation to use. The locale sets number
 * and date formats.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** The built-in languages a translation can start from. */
export const REPORT_LANGUAGES = ['EN', 'DE', 'FR'] as const
export type ReportLanguage = (typeof REPORT_LANGUAGES)[number]

/** Built-in labels per language (`en`, `de`, `fr`), each keyed by label name. */
export type ReportTranslationDefaults = Record<string, Record<string, string>>

export interface ReportTranslation {
  id: string
  name: string
  description: string
  locale: string
  defaultTranslation: ReportLanguage
  /** Only the labels that differ from the default language. */
  translations: Record<string, string>
  objectState: string
  createTime: string
  updateTime: string
  validationWarnings: ValidationWarning[]
}

export interface ReportTranslationInput {
  name: string
  description?: string
  locale?: string
  defaultTranslation?: ReportLanguage
  translations?: Record<string, string>
  objectState?: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapTranslation(r: Record<string, unknown>): ReportTranslation {
  const language = str(r.defaultTranslation).toUpperCase()
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    locale: str(r.locale) || 'en',
    defaultTranslation: (REPORT_LANGUAGES as readonly string[]).includes(language)
      ? (language as ReportLanguage)
      : 'EN',
    translations: (r.translations as Record<string, string>) ?? {},
    objectState: str(r.objectState),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface ReportTranslationListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

async function save(id: string, input: ReportTranslationInput, fallback: string) {
  const res = await apiFetch(`${API_BASE}/reporttranslations/${id}?context=${getContext()}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  })
  if (!res.ok) await throwApiError(res, fallback)
  return mapTranslation(await res.json())
}

export const ReportTranslationsApi = {
  getPage: async ({
    page = 1,
    pageSize = 25,
    search,
    status,
    sort,
  }: ReportTranslationListOptions = {}): Promise<ListPage<ReportTranslation>> => {
    const params = new URLSearchParams({
      ...searchParams('reportTranslations', search),
      context: getContext(),
      includeHidden: 'false',
      ...statusParams(status),
      ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/reporttranslations?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report translations')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapTranslation)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /** Every active translation, for the template's picker. */
  getAll: async (): Promise<ReportTranslation[]> =>
    (await ReportTranslationsApi.getPage({ pageSize: 100, status: 'ACTIVE' })).results,

  getById: async (id: string): Promise<ReportTranslation> => {
    const res = await apiFetch(`${API_BASE}/reporttranslations/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report translation')
    return mapTranslation(await res.json())
  },

  create: (id: string, input: ReportTranslationInput) =>
    save(id, input, 'Failed to create report translation'),

  update: (id: string, input: ReportTranslationInput) =>
    save(id, input, 'Failed to save report translation'),

  copy: (source: ReportTranslation, newId: string, name: string, description: string) =>
    save(
      newId,
      {
        name,
        description,
        locale: source.locale,
        defaultTranslation: source.defaultTranslation,
        translations: source.translations,
        objectState: 'ACTIVE',
      },
      'Failed to copy report translation'
    ),

  /** The API's own labels, per built-in language. Not network specific. */
  getDefaults: async (): Promise<ReportTranslationDefaults> => {
    const res = await apiFetch(`${API_BASE}/reporttranslationdefaults`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch default report labels')
    return ((await res.json()) as ReportTranslationDefaults) ?? {}
  },
}
